import { useEffect, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  XIcon,
} from '@heroicons/react/outline';
import {
  IncentiveCatalogReference,
  IncentiveItemEligibility,
  IncentiveItemSelectors,
} from 'types/incentive';
import { IncentiveProductPicker } from './IncentiveProductPicker';
import { IncentiveCollectionPicker } from './IncentiveCollectionPicker';
import {
  emptyIncentiveItemSelectors,
  normalizeIncentiveItemEligibility,
} from './IncentiveSettings.itemEligibility';

type SelectorKind = keyof IncentiveItemSelectors;
type SelectorPrefix = 'Eligible' | 'Excluded';
const conditionTypes: { kind: SelectorKind; label: string }[] = [
  { kind: 'products', label: 'Products' },
  { kind: 'skus', label: 'SKUs' },
  { kind: 'collections', label: 'Collections' },
  { kind: 'tags', label: 'Product tags' },
];
const splitList = (value: string) =>
  value
    .split(/[\n,]/)
    .map((part) => part.trim())
    .filter(Boolean);

const Chip = ({
  label,
  removeLabel,
  onRemove,
}: {
  label: string;
  removeLabel: string;
  onRemove: () => void;
}) => (
  <span className="inline-flex max-w-full items-center gap-1 rounded-md border border-gray-200 bg-gray-100 px-2 py-1 text-xs text-gray-700">
    <span className="break-all">{label}</span>
    <button
      type="button"
      aria-label={removeLabel}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onRemove}
      className="flex-shrink-0 rounded p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
    >
      <XIcon className="h-3 w-3" />
    </button>
  </span>
);

const TextChipsInput = ({
  label,
  chipLabel,
  values,
  onChange,
  placeholder,
}: {
  label: string;
  chipLabel: string;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
}) => {
  const [draft, setDraft] = useState('');
  const commit = (text = draft) => {
    const additions = splitList(text);
    if (additions.length) onChange([...values, ...additions]);
    setDraft('');
  };
  return (
    <div className="flex min-h-[36px] flex-wrap items-center gap-1.5 rounded-md border border-gray-300 bg-white p-1.5 focus-within:border-gray-500 focus-within:ring-1 focus-within:ring-gray-500">
      {values.map((value) => (
        <Chip
          key={value}
          label={value}
          removeLabel={`Remove ${chipLabel} ${value}`}
          onRemove={() => onChange(values.filter((entry) => entry !== value))}
        />
      ))}
      <input
        aria-label={label}
        type="text"
        value={draft}
        onChange={(event) => {
          const nextDraft = event.target.value;
          if (/[\n,]/.test(nextDraft)) commit(nextDraft);
          else setDraft(nextDraft);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          }
        }}
        onPaste={(event) => {
          const pasted = event.clipboardData.getData('text');
          if (/[\n,]/.test(pasted)) {
            event.preventDefault();
            const input = event.currentTarget;
            const start = input.selectionStart ?? input.value.length;
            const end = input.selectionEnd ?? start;
            commit(
              `${input.value.slice(0, start)}${pasted}${input.value.slice(
                end,
              )}`,
            );
          }
        }}
        onBlur={() => commit()}
        placeholder={placeholder}
        className="min-w-[100px] max-w-full flex-1 border-0 bg-transparent px-1 py-0.5 text-xs focus:outline-none focus:ring-0"
      />
    </div>
  );
};

type ConditionRowsProps = {
  prefix: SelectorPrefix;
  value: IncentiveItemSelectors;
  onChange: (value: IncentiveItemSelectors) => void;
  onPick: (kind: 'products' | 'collections') => void;
  storeId?: string;
};
const ConditionRows = ({
  prefix,
  value,
  onChange,
  onPick,
  storeId,
}: ConditionRowsProps) => {
  const [addedKinds, setAddedKinds] = useState<SelectorKind[]>([]);
  const visibleTypes = conditionTypes.filter(
    ({ kind }) => addedKinds.includes(kind) || value[kind].length > 0,
  );
  const availableTypes = conditionTypes.filter(
    ({ kind }) => !visibleTypes.some((visible) => visible.kind === kind),
  );
  const lowercasePrefix = prefix.toLowerCase();
  return (
    <div className="space-y-2">
      {visibleTypes.length === 0 ? (
        <p className="text-sm text-gray-500">
          {prefix === 'Eligible'
            ? 'Add a condition to choose eligible items.'
            : 'No exclusions added.'}
        </p>
      ) : null}
      {visibleTypes.map(({ kind, label }, index) => (
        <div key={kind}>
          {index > 0 ? (
            <div className="my-1 text-center text-[10px] font-semibold uppercase text-gray-400">
              Or
            </div>
          ) : null}
          <div
            role="group"
            aria-label={`${prefix} ${label} condition`}
            className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 rounded-md border border-gray-200 bg-white px-3 py-2 sm:grid-cols-[96px_minmax(0,1fr)_auto]"
          >
            <span className="text-xs font-semibold text-gray-700">{label}</span>
            <div className="col-span-2 row-start-2 min-w-0 sm:col-span-1 sm:col-start-2 sm:row-start-1">
              {kind === 'skus' || kind === 'tags' ? (
                <TextChipsInput
                  label={`${prefix} ${
                    kind === 'skus' ? 'SKUs' : 'product tags'
                  }`}
                  chipLabel={`${lowercasePrefix} ${
                    kind === 'skus' ? 'SKU' : 'product tag'
                  }`}
                  values={value[kind]}
                  onChange={(values) => onChange({ ...value, [kind]: values })}
                  placeholder={
                    kind === 'skus' ? 'Enter a SKU…' : 'Enter a product tag…'
                  }
                />
              ) : (
                <div className="flex flex-wrap items-center gap-1.5">
                  {value[kind].map((reference: IncentiveCatalogReference) => (
                    <Chip
                      key={reference.id}
                      label={reference.title}
                      removeLabel={`Remove ${lowercasePrefix} ${
                        kind === 'products' ? 'product' : 'collection'
                      } ${reference.title}`}
                      onRemove={() =>
                        onChange({
                          ...value,
                          [kind]: value[kind].filter(
                            ({ id }) => id !== reference.id,
                          ),
                        })
                      }
                    />
                  ))}
                  <button
                    type="button"
                    aria-label={`Select ${lowercasePrefix} ${kind}`}
                    disabled={!storeId}
                    onClick={() => onPick(kind)}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Select {kind}
                  </button>
                </div>
              )}
            </div>
            <button
              type="button"
              aria-label={`Remove ${lowercasePrefix} ${label} condition`}
              onClick={() => {
                setAddedKinds((current) =>
                  current.filter((entry) => entry !== kind),
                );
                onChange({ ...value, [kind]: [] });
              }}
              className="col-start-2 row-start-1 justify-self-end rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 sm:col-start-3"
            >
              <XIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}
      {availableTypes.length ? (
        <select
          aria-label={`Add ${lowercasePrefix} condition`}
          value=""
          onChange={(event) => {
            const selectedKind = event.target.value as SelectorKind;
            if (availableTypes.some(({ kind }) => kind === selectedKind))
              setAddedKinds((current) => [...current, selectedKind]);
          }}
          className="max-w-full rounded-md border border-gray-300 bg-white py-1.5 pl-3 pr-8 text-xs font-medium text-gray-700 focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500"
        >
          <option value="" disabled>
            Add condition…
          </option>
          {availableTypes.map(({ kind, label }) => (
            <option key={kind} value={kind}>
              {label}
            </option>
          ))}
        </select>
      ) : null}
    </div>
  );
};

export const IncentiveItemEligibilityEditor = ({
  value,
  storeId,
  ruleId,
  ruleKind = 'keep',
  onChange,
}: {
  value?: IncentiveItemEligibility;
  storeId?: string;
  ruleId: string;
  ruleKind?: 'keep' | 'bonus';
  onChange: (value: IncentiveItemEligibility) => void;
}) => {
  const eligibility = normalizeIncentiveItemEligibility(value);
  const hasInvalidSelectors =
    (eligibility.scope as string) === 'INVALID_SELECTORS';
  const [exclusionsExpanded, setExclusionsExpanded] = useState(false);
  const [picker, setPicker] = useState<{
    side: 'include' | 'exclude';
    kind: 'products' | 'collections';
  } | null>(null);
  const exclusionCount = Object.values(eligibility.exclude).reduce(
    (count, entries) => count + entries.length,
    0,
  );
  useEffect(() => {
    setExclusionsExpanded(false);
    setPicker(null);
  }, [storeId, ruleId]);
  const updateSide = (
    side: 'include' | 'exclude',
    selectors: IncentiveItemSelectors,
  ) =>
    onChange(
      normalizeIncentiveItemEligibility({ ...eligibility, [side]: selectors }),
    );
  const updateScope = (scope: IncentiveItemEligibility['scope']) => {
    if (!hasInvalidSelectors) onChange({ ...eligibility, scope });
  };
  return (
    <fieldset className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/50 p-3">
      <legend className="px-1 text-xs font-semibold uppercase text-gray-500">
        Eligible items
      </legend>
      <div className="flex flex-wrap gap-4 text-sm text-gray-700">
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name={`eligible-items-${ruleId}`}
            checked={eligibility.scope === 'ALL_ITEMS'}
            disabled={hasInvalidSelectors}
            onChange={() => updateScope('ALL_ITEMS')}
          />
          All items
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name={`eligible-items-${ruleId}`}
            checked={eligibility.scope === 'MATCHING_ITEMS'}
            disabled={hasInvalidSelectors}
            onChange={() => updateScope('MATCHING_ITEMS')}
          />
          Only matching items
        </label>
      </div>
      {eligibility.scope === 'MATCHING_ITEMS' ? (
        <div className="space-y-3">
          <p className="text-xs text-gray-500">
            Items must match any condition below.
          </p>
          <ConditionRows
            key={`include-${storeId}-${ruleId}`}
            prefix="Eligible"
            value={eligibility.include}
            onChange={(selectors) => updateSide('include', selectors)}
            onPick={(kind) => setPicker({ side: 'include', kind })}
            storeId={storeId}
          />
        </div>
      ) : null}
      {eligibility.scope !== 'ALL_ITEMS' &&
      eligibility.scope !== 'MATCHING_ITEMS' ? (
        <div role="alert" className="space-y-2 text-xs text-red-700">
          <p>
            {hasInvalidSelectors
              ? 'Item filters are invalid. Reset item filters to clear all conditions and exclusions, then rebuild them.'
              : 'Choose a supported eligible-items scope before saving.'}
          </p>
          {hasInvalidSelectors ? (
            <button
              type="button"
              className="font-medium underline hover:text-red-900"
              onClick={() => {
                onChange({
                  scope: 'ALL_ITEMS',
                  include: emptyIncentiveItemSelectors(),
                  exclude: emptyIncentiveItemSelectors(),
                });
                setExclusionsExpanded(false);
                setPicker(null);
              }}
            >
              Reset item filters
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="space-y-3 border-t border-gray-200 pt-3">
        <button
          type="button"
          aria-expanded={exclusionsExpanded}
          aria-controls={`item-exclusions-${ruleId}`}
          onClick={() => setExclusionsExpanded((expanded) => !expanded)}
          className="flex w-full items-center gap-2 text-left text-xs font-medium text-gray-700"
        >
          {exclusionsExpanded ? (
            <ChevronDownIcon className="h-4 w-4" />
          ) : (
            <ChevronRightIcon className="h-4 w-4" />
          )}
          Exclude items {exclusionCount ? `(${exclusionCount})` : '(optional)'}
        </button>
        {exclusionsExpanded ? (
          <div id={`item-exclusions-${ruleId}`} className="space-y-3 pl-1">
            <p className="text-xs text-gray-500">
              Excluded items are never eligible.
            </p>
            <ConditionRows
              key={`exclude-${storeId}-${ruleId}`}
              prefix="Excluded"
              value={eligibility.exclude}
              onChange={(selectors) => updateSide('exclude', selectors)}
              onPick={(kind) => setPicker({ side: 'exclude', kind })}
              storeId={storeId}
            />
            {exclusionCount > 0 ? (
              <button
                type="button"
                onClick={() => {
                  onChange({
                    ...eligibility,
                    exclude: emptyIncentiveItemSelectors(),
                  });
                  setExclusionsExpanded(false);
                }}
                className="text-xs text-gray-600 underline hover:text-gray-900"
              >
                Remove exclusions
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      <p className="text-xs leading-relaxed text-gray-500">
        {ruleKind === 'bonus' ? 'Every original item selected must qualify for the same bonus rule.' : 'Every item selected in a request must qualify for the same keep-item rule.'} Product tags are different from order and customer tags.
      </p>
      {eligibility.scope === 'MATCHING_ITEMS' || exclusionsExpanded ? (
        <p className="text-[11px] text-gray-500">
          SKUs and product tags match exactly, ignoring letter case. Use Enter
          or commas to add values.
        </p>
      ) : null}
      {storeId && picker?.kind === 'products' ? (
        <IncentiveProductPicker
          storeId={storeId}
          selected={eligibility[picker.side].products}
          onChange={(products) =>
            updateSide(picker.side, { ...eligibility[picker.side], products })
          }
          onClose={() => setPicker(null)}
        />
      ) : null}
      {storeId && picker?.kind === 'collections' ? (
        <IncentiveCollectionPicker
          storeId={storeId}
          selected={eligibility[picker.side].collections}
          onChange={(collections) =>
            updateSide(picker.side, {
              ...eligibility[picker.side],
              collections,
            })
          }
          onClose={() => setPicker(null)}
        />
      ) : null}
    </fieldset>
  );
};
